import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl, apiRequest } from '../../config/api';
import SuccessToast from '../SuccessToast';
import ConfirmDialog from '../ConfirmDialog';
import { cronPresets, describeCron } from '../../utils/cron';

function ScheduledTasksPanel({ selectedBotId, selectedBotName, appliedTaskTimezone }) {
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Estado para confirmar ejecución de tarea programada
  const [confirmExecuteTask, setConfirmExecuteTask] = useState(null);

  // Estados para tareas programadas
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ taskName: '', description: '', script: '', cronExpression: '0 */5 * * * *', botId: null, timezone: 'Europe/Madrid' });
  const [taskCronPreset, setTaskCronPreset] = useState('5min');
  const [taskValidation, setTaskValidation] = useState(null);
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskExecuting, setTaskExecuting] = useState(null);
  const [taskExecuteResult, setTaskExecuteResult] = useState(null);
  const [showTaskHistoryModal, setShowTaskHistoryModal] = useState(false);
  const [taskHistoryData, setTaskHistoryData] = useState(null);
  const [taskHistoryName, setTaskHistoryName] = useState('');
  const [taskEditorExpanded, setTaskEditorExpanded] = useState(false);
  const [scriptSaveMsg, setScriptSaveMsg] = useState('');

  const mostrarExito = (mensaje) => {
    setToastError(null);
    setToastExito(mensaje);
  };

  const mostrarError = (mensaje) => {
    setToastExito(null);
    setToastError(mensaje);
  };

  // ===== FUNCIONES DE TAREAS PROGRAMADAS =====

  const loadScheduledTasks = useCallback(async () => {
    if (!selectedBotId) {
      setScheduledTasks([]);
      return;
    }
    try {
      const response = await apiRequest(getApiUrl(`/scheduled-tasks?botId=${selectedBotId}`));
      if (response.ok) {
        const data = await response.json();
        setScheduledTasks(data);
      }
    } catch (error) {
      console.error('Error loading scheduled tasks:', error);
    }
  }, [selectedBotId]);

  useEffect(() => {
    loadScheduledTasks();
  }, [loadScheduledTasks]);

  const skipInitialTimezoneApplyRef = useRef(true);
  useEffect(() => {
    if (skipInitialTimezoneApplyRef.current) {
      skipInitialTimezoneApplyRef.current = false;
      return;
    }
    if (!appliedTaskTimezone) return;
    setScheduledTasks(prev => prev.map(t =>
      Number(t.botId) === Number(appliedTaskTimezone.botId) ? { ...t, timezone: appliedTaskTimezone.timezone } : t
    ));
  }, [appliedTaskTimezone]);

  const openNewTaskModal = () => {
    setEditingTask(null);
    setTaskForm({ taskName: '', description: '', script: '', cronExpression: '0 */5 * * * *', botId: selectedBotId, timezone: 'Europe/Madrid' });
    setTaskCronPreset('5min');
    setTaskValidation(null);
    setTaskExecuteResult(null);
    setTaskEditorExpanded(false);
    setScriptSaveMsg('');
    setShowTaskModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setTaskEditorExpanded(false);
    setScriptSaveMsg('');
  };

  const openEditTaskModal = (task) => {
    setEditingTask(task);
    setTaskForm({
      taskName: task.taskName,
      description: task.description || '',
      script: task.script || '',
      cronExpression: task.cronExpression,
      botId: selectedBotId,
      timezone: task.timezone || 'Europe/Madrid'
    });
    const matchedPreset = cronPresets.find(p => p.cron === task.cronExpression);
    setTaskCronPreset(matchedPreset ? matchedPreset.key : 'custom');
    setTaskValidation(null);
    setTaskExecuteResult(null);
    setTaskEditorExpanded(false);
    setScriptSaveMsg('');
    setShowTaskModal(true);
  };

  useEffect(() => {
    if (!showTaskModal || !taskEditorExpanded) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setTaskEditorExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showTaskModal, taskEditorExpanded]);

  const handleCronPresetChange = (presetKey) => {
    setTaskCronPreset(presetKey);
    const preset = cronPresets.find(p => p.key === presetKey);
    if (preset && preset.cron) {
      setTaskForm(prev => ({ ...prev, cronExpression: preset.cron }));
    }
  };

  const handleValidateTask = async () => {
    setTaskValidation(null);
    try {
      const response = await apiRequest(getApiUrl('/scheduled-tasks/validate'), {
        method: 'POST',
        body: JSON.stringify({ script: taskForm.script })
      });
      const data = await response.json();
      setTaskValidation(data);
    } catch (error) {
      setTaskValidation({ valid: false, error: 'Error de conexión' });
    }
  };

  const handleSaveTask = async () => {
    setTaskSaving(true);
    setTaskValidation(null);
    try {
      const url = editingTask
        ? getApiUrl(`/scheduled-tasks/${editingTask.id}`)
        : getApiUrl('/scheduled-tasks');
      const method = editingTask ? 'PUT' : 'POST';

      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(taskForm)
      });

      if (response.ok) {
        closeTaskModal();
        loadScheduledTasks();
        mostrarExito(editingTask ? 'Tarea actualizada' : 'Tarea creada');
      } else {
        const data = await response.json();
        setTaskValidation({ valid: false, error: data.error || 'Error al guardar' });
      }
    } catch (error) {
      setTaskValidation({ valid: false, error: 'Error de conexión' });
    } finally {
      setTaskSaving(false);
    }
  };

  const handleSaveScript = async () => {
    if (!editingTask) return;
    setTaskSaving(true);
    setScriptSaveMsg('');
    setTaskValidation(null);
    try {
      const response = await apiRequest(getApiUrl(`/scheduled-tasks/${editingTask.id}`), {
        method: 'PUT',
        body: JSON.stringify({
          taskName: editingTask.taskName,
          description: editingTask.description || '',
          script: taskForm.script,
          cronExpression: editingTask.cronExpression,
          botId: selectedBotId,
          timezone: editingTask.timezone || 'Europe/Madrid',
        }),
      });
      if (response.ok) {
        setEditingTask((prev) => ({ ...prev, script: taskForm.script }));
        setScriptSaveMsg('Script guardado');
        loadScheduledTasks();
      } else {
        const data = await response.json();
        setTaskValidation({ valid: false, error: data.error || 'Error al guardar' });
      }
    } catch (error) {
      setTaskValidation({ valid: false, error: 'Error de conexión' });
    } finally {
      setTaskSaving(false);
    }
  };

  const handleDeleteTask = async (id) => {
    setTaskToDelete(null);
    try {
      const response = await apiRequest(getApiUrl(`/scheduled-tasks/${id}`), { method: 'DELETE' });
      if (response.ok) {
        loadScheduledTasks();
        mostrarExito('Tarea eliminada');
      } else {
        const err = await response.json().catch(() => ({}));
        mostrarError(err.error || 'Error al eliminar tarea');
      }
    } catch (error) {
      mostrarError('Error al eliminar tarea');
    }
  };

  const handleToggleTask = async (id) => {
    try {
      const response = await apiRequest(getApiUrl(`/scheduled-tasks/${id}/toggle`), { method: 'PATCH' });
      if (response.ok) {
        loadScheduledTasks();
      } else {
        mostrarError('Error al cambiar estado');
      }
    } catch (error) {
      mostrarError('Error al cambiar estado');
    }
  };

  const requestExecuteTask = (task) => {
    setConfirmExecuteTask({ id: task.id, name: task.taskName });
  };

  const handleExecuteTask = async (id) => {
    setTaskExecuting(id);
    setTaskExecuteResult(null);
    setConfirmExecuteTask(null);
    try {
      const response = await apiRequest(getApiUrl(`/scheduled-tasks/${id}/execute`), { method: 'POST' });
      const data = await response.json();
      setTaskExecuteResult({ taskId: id, ...data });
      loadScheduledTasks();
    } catch (error) {
      setTaskExecuteResult({ taskId: id, success: false, error: 'Error de conexión' });
    } finally {
      setTaskExecuting(null);
    }
  };

  const openTaskHistory = async (task) => {
    setTaskHistoryName(task.taskName);
    setTaskHistoryData(null);
    setShowTaskHistoryModal(true);
    try {
      const response = await apiRequest(getApiUrl(`/scheduled-tasks/${task.id}/stats`));
      if (response.ok) {
        const data = await response.json();
        setTaskHistoryData(data);
      }
    } catch (error) {
      console.error('Error loading task history:', error);
    }
  };

  return (
    <>
        {/* ===== SECCIÓN DE TAREAS PROGRAMADAS ===== */}
        <div className="admin-panel config-tasks-panel">
          <div className="admin-header">
            <h2>Tareas programadas</h2>
            <button className="admin-btn primary" onClick={openNewTaskModal}>
              + Nueva tarea
            </button>
          </div>
          
          <p className="script-section-desc">
            Funciones que se ejecutan automáticamente según la programación cron. Tienen acceso a <code>http</code>, <code>whatsapp</code>, <code>calendar</code>, <code>sheets</code> y <code>vars.botCache</code>.
          </p>

          <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
          <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />

          {scheduledTasks.length > 0 ? (
            <div className="admin-users-list">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Programación</th>
                    <th>Estado</th>
                    <th>Última ejecución</th>
                    <th>Resultado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledTasks.map(task => {
                    return (
                    <tr key={task.id}>
                      <td>
                        <strong>{task.taskName}</strong>
                        {task.description && <div style={{fontSize: '11px', color: '#888', marginTop: '2px'}}>{task.description}</div>}
                      </td>
                      <td>
                        <code style={{fontSize: '11px'}}>{describeCron(task.cronExpression)}</code>
                        <div style={{fontSize: '10px', color: '#888', marginTop: '2px'}}>
                          {task.timezone || 'Europe/Madrid'}
                          <span style={{ marginLeft: '4px', color: '#aaa' }}>(del bot)</span>
                        </div>
                      </td>
                      <td>
                        <span 
                          className={`role-badge ${task.isActive ? 'admin' : 'user'}`}
                          style={{cursor: 'pointer'}}
                          onClick={() => handleToggleTask(task.id)}
                          title={task.isActive ? 'Click para desactivar' : 'Click para activar'}
                        >
                          {task.isActive ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td style={{fontSize: '12px'}}>
                        {task.lastExecutionAt 
                          ? new Date(task.lastExecutionAt).toLocaleString('es-ES', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})
                          : '-'}
                      </td>
                      <td>
                        {task.lastStatus && (
                          <span className={`script-badge ${task.lastStatus === 'SUCCESS' ? 'has-script' : 'no-script'}`}>
                            {task.lastStatus === 'SUCCESS' ? 'OK' : 'Error'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <button className="admin-btn-small" onClick={() => openEditTaskModal(task)}>
                            Editar
                          </button>
                          <button 
                            className="admin-btn-small"
                            style={{ background: '#e0f2fe', color: '#0369a1' }}
                            onClick={() => openTaskHistory(task)}
                          >
                            Historial
                          </button>
                          <button 
                            className="admin-btn-small"
                            style={{ background: '#f0fdf4', color: '#15803d' }}
                            onClick={() => requestExecuteTask(task)}
                            disabled={taskExecuting === task.id}
                          >
                            {taskExecuting === task.id ? '...' : 'Ejecutar'}
                          </button>
                          <button 
                            className="admin-btn-small"
                            style={{ background: '#fee2e2', color: '#dc2626' }}
                            onClick={() => setTaskToDelete({ id: task.id, name: task.taskName })}
                          >
                            Eliminar
                          </button>
                        </div>
                        {taskExecuteResult && taskExecuteResult.taskId === task.id && (
                          <div className={`script-validation ${taskExecuteResult.success ? 'valid' : 'invalid'}`} style={{marginTop: '6px', fontSize: '11px'}}>
                            {taskExecuteResult.success
                              ? `OK (${taskExecuteResult.executionTimeMs}ms)`
                              : `Error: ${taskExecuteResult.error}`}
                            {taskExecuteResult.logs && taskExecuteResult.logs.length > 0 && (
                              <pre style={{margin: '4px 0 0', whiteSpace: 'pre-wrap', fontSize: '10px'}}>{taskExecuteResult.logs.join('\n')}</pre>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">No hay tareas programadas</p>
          )}
        </div>

        {/* Modal para crear/editar tarea programada */}
        {showTaskModal && createPortal(
          <div className="modal-overlay" onClick={taskEditorExpanded ? undefined : closeTaskModal}>
            <div
              className="modal-content script-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="task-modal-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="task-modal-title">{editingTask ? `Editar: ${editingTask.taskName}` : 'Nueva tarea programada'}</h3>
                <button className="modal-close" onClick={closeTaskModal}>×</button>
              </div>
              <div className="script-editor-body">
                <div className="form-group">
                  <label htmlFor="task-nombre">Nombre</label>
                  <input
                    id="task-nombre"
                    type="text"
                    value={taskForm.taskName}
                    onChange={e => setTaskForm({...taskForm, taskName: e.target.value})}
                    placeholder="Ej: Sync Google Sheets"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Descripción (opcional)</label>
                  <input
                    type="text"
                    value={taskForm.description}
                    onChange={e => setTaskForm({...taskForm, description: e.target.value})}
                    placeholder="Breve descripción de lo que hace"
                  />
                </div>

                <div className="form-group">
                  <label>Programación</label>
                  <div className="cron-presets">
                    {cronPresets.map(preset => (
                      <button
                        key={preset.key}
                        type="button"
                        className={`cron-preset-btn ${taskCronPreset === preset.key ? 'active' : ''}`}
                        onClick={() => handleCronPresetChange(preset.key)}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  {taskCronPreset === 'custom' && (
                    <input
                      type="text"
                      value={taskForm.cronExpression}
                      onChange={e => setTaskForm({...taskForm, cronExpression: e.target.value})}
                      placeholder="0 */5 * * * *"
                      style={{ marginTop: '0.5rem', fontFamily: 'monospace' }}
                    />
                  )}
                  <div style={{fontSize: '11px', color: '#888', marginTop: '4px'}}>
                    Cron: <code>{taskForm.cronExpression}</code> (segundo minuto hora día mes díaSemana)
                  </div>
                </div>

                <div className="form-group">
                  <label>Zona horaria</label>
                  <div style={{fontSize: '11px', color: '#888', marginTop: '4px'}}>
                    Heredada del bot {selectedBotName}
                  </div>
                </div>

                <div className="script-help">
                  <details>
                    <summary>Ayuda: API disponible en tareas programadas</summary>
                    <div className="script-help-content">
                      <p>Define una función <code>execute()</code> que se ejecutará según la programación.</p>
                      <p>Los bridges devuelven datos de JavaScript: no hace falta <code>JSON.parse</code>.</p>
                      
                      <p><strong>HTTP:</strong> <code>get</code> y <code>post</code> devuelven <code>{'{ status, headers, body }'}</code>. <code>status</code> es el código. <code>headers</code> usa nombres en minúsculas. <code>body</code> viene parseado si la respuesta es JSON; si no, es un string, o <code>null</code> si el cuerpo va vacío. En <code>post</code>, un objeto o un array se envía como JSON y un string tal cual.</p>
                      <pre>{`// GET
var r = http.get("https://api.example.com/data");
var status = r.status;
var data = r.body;

// GET con headers
var conCabeceras = http.get("https://api.example.com/data", {
  "Authorization": "Bearer API_KEY"
});

// POST
var creado = http.post("https://api.example.com/send", { key: "value" });

// POST con headers
var creadoConCabeceras = http.post("https://api.example.com/send",
  { key: "value" },
  { "Authorization": "Bearer TOKEN" });`}</pre>

                      <p><strong>db:</strong> <code>save</code> recibe un objeto o un array. <code>find</code>, <code>findByUser</code>, <code>findByPhone</code> y <code>findWhere</code> devuelven un array de registros con <code>data</code> ya parseado.</p>
                      <pre>{`db.save("c", { a: 1 });
var valor = db.find("c")[0].data.a;
var delDia = db.findWhere("c", { fecha: "2026-09-30", n: 3 });`}</pre>

                      <p><strong>WhatsApp:</strong> <code>sendTemplate</code> recibe los componentes como array (o <code>null</code>) y devuelve un booleano. <code>sendTemplateBulk</code> recibe los destinatarios como array y devuelve un objeto; si falla, ese objeto tiene <code>error</code>.</p>
                      <pre>{`// Enviar plantilla
var sent = whatsapp.sendTemplate(
  "34600000000",          // teléfono
  "nombre_plantilla",     // nombre template
  "es",                   // idioma
  [{
    type: "body",
    parameters: [
      { type: "text", text: "valor1" },
      { type: "text", text: "valor2" }
    ]
  }],
  1                       // botId (se ignora: la tarea ya tiene el suyo)
);

// Envío masivo
var destinatarios = [
  {
    recipientPhoneNumber: "34600000001",
    components: [{ type: "body", parameters: [{ type: "text", text: "valor1" }] }]
  },
  {
    recipientPhoneNumber: "34600000002",
    components: [{ type: "body", parameters: [{ type: "text", text: "valor2" }] }]
  }
];
var result = whatsapp.sendTemplateBulk(destinatarios, "nombre_plantilla", "es", 1);
if (result.error) {
  console.log(result.error);
}
console.log(result.successCount + "/" + result.total);`}</pre>

                      <p><strong>calendar</strong> — <code>freeBusy</code>, <code>listEvents</code>, <code>getEvent</code>, <code>createEvent</code> y <code>updateEvent</code> devuelven un array o un objeto de JavaScript, sin <code>JSON.parse</code>. <code>deleteEvent</code> no devuelve nada. Las fechas son un string ISO-8601 o un <code>Date</code>. El calendario tiene que estar compartido con la cuenta de servicio y dado de alta en el panel Google del bot.</p>
                      <pre>{`var huecos = calendar.freeBusy("usuario@gmail.com", "2026-10-01T08:00:00Z", "2026-10-01T21:00:00Z");
var inicio = huecos[0].start;

var eventos = calendar.listEvents("usuario@gmail.com");
var titulo = eventos[0].summary;`}</pre>

                      <p><strong>sheets</strong> — <code>read</code> devuelve un array de filas. <code>append</code>, <code>update</code> y <code>clear</code> devuelven un objeto. La hoja tiene que estar compartida con la cuenta de servicio y dada de alta en el panel Google del bot.</p>
                      <pre>{`var filas = sheets.read("1AbCdEfGhIjKlMnOpQr", "Hoja1!A2:D100");
for (var i = 0; i < filas.length; i++) {
  var phone = filas[i][0];
  var name = filas[i][1];
  var sent = whatsapp.sendTemplate(
    phone, "mi_plantilla", "es",
    [{
      type: "body",
      parameters: [{ type: "text", text: name }]
    }],
    1
  );
  console.log(phone + ": " + (sent ? "OK" : "FAIL"));
}`}</pre>

                      <p><strong>vars</strong> — una tarea solo ve <code>vars.botCache</code>, las variables que comparten todos los chats del bot. Caducan a las 24 horas, o a los segundos que digas en el <code>ttl</code>, como mucho 30 días. <code>vars.context</code> y <code>vars.cache</code> son de cada chat: en una tarea lanzan <code>vars: no disponible en tareas programadas</code>, y como al guardar no se ejecuta <code>execute()</code>, el error aparece en la primera ejecución. Los administradores ven, editan y borran estas variables en Configuración.</p>
                      <pre>{`var ultima = vars.botCache.get("ultima_sync");        // null la primera vez
console.log("Última sincronización: " + ultima);
vars.botCache.set("ultima_sync", new Date().toISOString(), 172800); // 2 días
vars.botCache.incr("syncs");`}</pre>
                      <p><strong>Límites:</strong> Timeout 60s, script max 128KB; 500 operaciones de vars por ejecución</p>
                    </div>
                  </details>
                </div>

                {!taskEditorExpanded ? (
                  <textarea
                    className="script-textarea"
                    value={taskForm.script}
                    onChange={e => {
                      setTaskForm({...taskForm, script: e.target.value});
                      setTaskValidation(null);
                    }}
                    placeholder={`function execute() {\n  // Tu lógica aquí...\n  console.log("Tarea ejecutada");\n}`}
                    spellCheck={false}
                  />
                ) : null}

                {!taskEditorExpanded && taskValidation ? (
                  <div className={`script-validation ${taskValidation.valid ? 'valid' : 'invalid'}`}>
                    {taskValidation.valid ? 'Script válido' : `Error: ${taskValidation.error}`}
                  </div>
                ) : null}
                
                <div className="script-actions">
                  <div className="script-actions-left">
                    {editingTask ? (
                      <button
                        type="button"
                        className="admin-btn secondary"
                        onClick={() => {
                          setScriptSaveMsg('');
                          setTaskEditorExpanded(true);
                        }}
                      >
                        Ampliar
                      </button>
                    ) : null}
                    <button
                      className="admin-btn secondary"
                      onClick={handleValidateTask}
                      disabled={!taskForm.script.trim() || taskSaving}
                    >
                      Validar
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      className="admin-btn secondary"
                      onClick={closeTaskModal}
                    >
                      Cancelar
                    </button>
                    <button 
                      className="admin-btn primary"
                      onClick={handleSaveTask}
                      disabled={!taskForm.taskName.trim() || !taskForm.script.trim() || !taskForm.cronExpression.trim() || taskSaving}
                    >
                      {taskSaving ? 'Guardando...' : (editingTask ? 'Guardar ajustes' : 'Crear tarea')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        , document.body)}

        {taskEditorExpanded && createPortal(
          <div
            className="script-editor-inline is-expanded"
            role="dialog"
            aria-modal="true"
            aria-label="Editor de script"
          >
            <div className="script-editor-body">
              <textarea
                className="script-textarea"
                value={taskForm.script}
                onChange={e => {
                  setTaskForm({ ...taskForm, script: e.target.value });
                  setTaskValidation(null);
                  setScriptSaveMsg('');
                }}
                placeholder={`function execute() {\n  // Tu lógica aquí...\n  console.log("Tarea ejecutada");\n}`}
                spellCheck={false}
              />
              {taskValidation ? (
                <div className={`script-validation ${taskValidation.valid ? 'valid' : 'invalid'}`}>
                  {taskValidation.valid ? 'Script válido' : `Error: ${taskValidation.error}`}
                </div>
              ) : null}
              {scriptSaveMsg ? <div className="admin-alert success">{scriptSaveMsg}</div> : null}
              <div className="script-actions">
                <div className="script-actions-left">
                  <button
                    type="button"
                    className="admin-btn secondary"
                    onClick={() => setTaskEditorExpanded(false)}
                  >
                    Reducir
                  </button>
                  <button
                    type="button"
                    className="admin-btn secondary"
                    onClick={handleValidateTask}
                    disabled={!taskForm.script.trim() || taskSaving}
                  >
                    Validar
                  </button>
                </div>
                <button
                  type="button"
                  className="admin-btn primary"
                  onClick={handleSaveScript}
                  disabled={!taskForm.script.trim() || taskSaving}
                >
                  {taskSaving ? 'Guardando...' : 'Guardar script'}
                </button>
              </div>
            </div>
          </div>
        , document.body)}

        {/* Modal de historial de tarea */}
        {showTaskHistoryModal && createPortal(
          <div className="modal-overlay" onClick={() => setShowTaskHistoryModal(false)}>
            <div className="modal-content script-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Historial — {taskHistoryName}</h3>
                <button className="modal-close" onClick={() => setShowTaskHistoryModal(false)}>×</button>
              </div>
              <div className="script-editor-body">
                {!taskHistoryData ? (
                  <div className="admin-loading">Cargando...</div>
                ) : (
                  <>
                    <div className="script-stats-grid">
                      <div className="script-stat">
                        <span className="script-stat-value">{taskHistoryData.totalExecutions}</span>
                        <span className="script-stat-label">Ejecuciones</span>
                      </div>
                      <div className="script-stat">
                        <span className="script-stat-value">{taskHistoryData.avgExecutionTimeMs}ms</span>
                        <span className="script-stat-label">Tiempo medio</span>
                      </div>
                      <div className="script-stat">
                        <span className="script-stat-value" style={{color: taskHistoryData.errorsLast24h > 0 ? '#dc2626' : '#16a34a'}}>
                          {taskHistoryData.errorsLast24h}
                        </span>
                        <span className="script-stat-label">Errores (24h)</span>
                      </div>
                      <div className="script-stat">
                        <span className="script-stat-value">{taskHistoryData.errorRate}%</span>
                        <span className="script-stat-label">Tasa error</span>
                      </div>
                    </div>

                    {taskHistoryData.recentExecutions && taskHistoryData.recentExecutions.length > 0 ? (
                      <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                        <table className="admin-table" style={{fontSize: '12px'}}>
                          <thead>
                            <tr>
                              <th>Fecha</th>
                              <th>Estado</th>
                              <th>Tiempo</th>
                              <th>Detalle</th>
                            </tr>
                          </thead>
                          <tbody>
                            {taskHistoryData.recentExecutions.map((exec, i) => (
                              <tr key={i}>
                                <td>{new Date(exec.createdAt).toLocaleString('es-ES', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit'})}</td>
                                <td>
                                  <span className={`script-badge ${exec.status === 'SUCCESS' ? 'has-script' : 'no-script'}`}>
                                    {exec.status === 'SUCCESS' ? 'OK' : 'Error'}
                                  </span>
                                </td>
                                <td>{exec.executionTimeMs}ms</td>
                                <td style={{maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                  {exec.errorDetail && <div style={{color: '#dc2626'}}>{exec.errorDetail}</div>}
                                  {exec.logs && (
                                    <details>
                                      <summary style={{cursor:'pointer', color: '#4f46e5', fontSize: '11px'}}>Logs</summary>
                                      <pre style={{fontSize: '10px', whiteSpace: 'pre-wrap', background: '#1f2937', color: '#e5e7eb', padding: '6px', borderRadius: '4px', marginTop: '4px'}}>{exec.logs}</pre>
                                    </details>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-data">Sin ejecuciones registradas</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        , document.body)}

        {/* Modal de confirmación para ejecutar tarea programada */}
        {taskToDelete && (
          <ConfirmDialog
            title="Eliminar tarea"
            note="Esta acción no se puede deshacer."
            onCancel={() => setTaskToDelete(null)}
            onConfirm={() => handleDeleteTask(taskToDelete.id)}
          >
            ¿Eliminar la tarea programada <strong>"{taskToDelete.name}"</strong>?
          </ConfirmDialog>
        )}

        {confirmExecuteTask && createPortal(
          <div className="modal-overlay" onClick={() => setConfirmExecuteTask(null)}>
            <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Confirmar ejecución</h3>
                <button className="modal-close" onClick={() => setConfirmExecuteTask(null)}>×</button>
              </div>
              <div className="confirm-modal-body">
                <p>
                  ¿Seguro que deseas ejecutar la tarea <strong>"{confirmExecuteTask.name}"</strong> ahora?
                </p>
                <p className="confirm-modal-note">
                  La tarea se lanzará inmediatamente, fuera de su programación habitual.
                </p>
              </div>
              <div className="confirm-modal-actions">
                <button
                  type="button"
                  className="admin-btn secondary"
                  onClick={() => setConfirmExecuteTask(null)}
                  disabled={taskExecuting === confirmExecuteTask.id}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="admin-btn primary"
                  onClick={() => handleExecuteTask(confirmExecuteTask.id)}
                  disabled={taskExecuting === confirmExecuteTask.id}
                >
                  {taskExecuting === confirmExecuteTask.id ? 'Ejecutando...' : 'Sí, ejecutar'}
                </button>
              </div>
            </div>
          </div>
        , document.body)}
    </>
  );
}

export default ScheduledTasksPanel;
